/* React */
import React, { useEffect } from "react";

/* React Router  */
import { Route } from "react-router-dom";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { clearAuthState } from "src/redux/features/auth/authSlice";
import { setToastMessage } from "src/redux/features/toast-message/toastMessageSlice";

/* Ionic */
import {
  IonApp,
  IonRouterOutlet,
  setupIonicReact,
} from "@ionic/react";

import { IonReactRouter } from "@ionic/react-router";

/* Cookies */
import Cookies from "js-cookie";

/* Google */
import { GoogleOAuthProvider } from "@react-oauth/google";

/* Thunks */
import { providerInfo } from "src/redux/thunks/auth/providerThunk";
import { refreshAccessToken } from "src/redux/thunks/auth/authThunk";
import { fetchBackendSettings } from "src/redux/thunks/settings/backendSettingsThunk";

/* Utils */
import ProtectedRoute from "./utils/routes/protected-route/ProtectedRoute";
import ModalWrapper from "./utils/wrapper/modal-wrapper/ModalWrapper";

/* Pages */
import RedirectHandler from "src/components/redirect-handler/RedirectHandler";
import Welcome from "src/pages/welcome/Welcome";
import Insights from "src/pages/insights/Insights";
import Imprint from "src/pages/imprint/Imprint";
import AdminDashboard from "src/pages/admin-dashboard/AdminDashboard";
import Glossary from "src/pages/glossary/Glossary";

/* Components */
import ToastMessage from "src/components/shared/toast-message/ToastMessage";
import MainMenu from "src/components/menus/main-menu/MainMenu";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variable.scss";

/* Stylesheet */
import "./theme/globals.scss";
import "simplebar-react/dist/simplebar.min.css";

/* Constants */
import { Paths } from "src/shared/constants/common/Paths";

/* Interfaces */
import { TokenType } from "src/shared/interfaces/api/Token";

setupIonicReact();

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const providerState = useSelector(
    (state: RootState) => state.provider
  );

  const { data: providerData } = providerState;

  const authState = useSelector(
    (state: RootState) => state.auth
  );

  const { data: authData, success } = authState;

  useEffect(() => {
    dispatch(providerInfo());
  }, [dispatch]);

  useEffect(() => {
    if (!authData?.isAuthenticated) return;
    dispatch(fetchBackendSettings());
  }, [dispatch, authData?.isAuthenticated]);

  useEffect(() => {
    const accessToken = Cookies.get(TokenType.ACCESS_TOKEN);
    const refreshToken = Cookies.get(
      TokenType.REFRESH_TOKEN
    );

    if (!accessToken || !refreshToken) {
      dispatch(clearAuthState());
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      authData?.isAuthenticated &&
      authData.expirationTime
    ) {
      const currentTime = Date.now();
      const timeUntilExpiration =
        authData.expirationTime - currentTime;

      if (timeUntilExpiration > 0) {
        const timeout = setTimeout(() => {
          dispatch(refreshAccessToken());
        }, timeUntilExpiration);

        return () => clearTimeout(timeout);
      }
    }
  }, [
    dispatch,
    authData?.isAuthenticated,
    authData?.expirationTime,
  ]);

  useEffect(() => {
    if (success) {
      dispatch(
        setToastMessage({
          type: success.type,
          message: success.message,
          show: true,
        })
      );
    }
  }, [dispatch, success]);

  return (
    <GoogleOAuthProvider
      clientId={providerData!.google.clientId}
    >
      <ToastMessage />
      <ModalWrapper />
      <IonApp>
        <IonReactRouter>
          <MainMenu />
          <IonRouterOutlet id="main-content">
            <Route
              path="/redirect"
              component={RedirectHandler}
            />
            <Route
              exact={true}
              path={Paths.HOME}
              component={Welcome}
            />
            <Route
              exact={true}
              path={Paths.GLOSSARY}
              component={Glossary}
            />
            <Route
              exact={true}
              path={Paths.IMPRINT}
              component={Imprint}
            />
            <ProtectedRoute
              exact={false}
              path={`${Paths.INSIGHTS}`}
              component={Insights}
            />
            <ProtectedRoute
              exact={false}
              path={`${Paths.ADMIN_DASHBOARD}`}
              component={AdminDashboard}
            />
          </IonRouterOutlet>
        </IonReactRouter>
      </IonApp>
    </GoogleOAuthProvider>
  );
};

export default App;
